import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { useApi } from "@/src/api/useApi";
import NoData from "@/src/components/NoData";
import PaginationScreen from "@/src/screen/PaginationView";
import MyButton from "@/src/screen/ui/MyButton";
import useLoginStore from "@/src/store/useLoginStore";
import useUserStore from "@/src/store/useUser";
import { getTimeAgo } from "@/src/tools";
import type { CommentListType } from "@/src/types/comment";
import dynamic from "next/dynamic";
import Image from "next/image";
import { useCallback, useEffect, useMemo, useState } from "react";
import FavoriteComment from "./FavoriteComment";
import RatingComment from "./RatingComment";

const AddComment = dynamic(() => import("./AddComment"), { ssr: false });

type SortOption = "new" | "old";

interface CommentListProps {
  productId: number;
  productIId: number;
}

export default function CommentList({
  productId,
  productIId,
}: CommentListProps) {
  const { dataUser } = useUserStore();
  const { openLogin } = useLoginStore();

  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<SortOption>("new");
  const [replyTo, setReplyTo] = useState<number | null>(null);
  const [showImages, setShowImages] = useState(true);
  const [showAddComment, setShowAddComment] = useState(false);

  const { data, isLoading, error, refresh } = useApi<CommentListType>(
    `/comment-list?page=${page}&size=10&type=product&prod=${productId}&sort=${sort}`
  );

  const total = data?.data.count ?? 0;
  const rows = data?.data.rows ?? [];

  const openAddComment = useCallback(
    (parentId: number | null, withImages = true) => {
      if (!dataUser?.token) {
        openLogin();
      } else {
        setReplyTo(parentId);
        setShowImages(withImages);
        setShowAddComment(true);
      }
    },
    [dataUser, openLogin]
  );

  const onSuccess = useCallback(() => {
    setReplyTo(null);
    setShowAddComment(false);
    refresh();
  }, [refresh]);

  useEffect(() => {
    setPage(1);
  }, [sort]);

  const renderSkeleton = () => (
    <div className="space-y-4">
      {Array.from({ length: 3 }).map((_, i) => (
        <Skeleton key={i} className="h-24 w-full rounded-2xl" />
      ))}
    </div>
  );

  const SortButton = ({
    label,
    value,
  }: {
    label: string;
    value: SortOption;
  }) => (
    <MyButton
      color={value === sort ? "primary" : "light"}
      variant={value === sort ? "solid" : "outline"}
      className="rounded-full px-4"
      onClick={() => setSort(value)}
    >
      {label}
    </MyButton>
  );

  const commentList = useMemo(() => {
    if (rows.length === 0) return null;

    return rows.map((comment) => (
      <article
        key={comment.id}
        className="p-3 space-y-2 bg-white m-2 rounded-xl shadow-sm border border-[#23657B] transition-all max-w-4xl mx-auto"
      >
        <header className="flex flex-wrap justify-between items-center gap-2">
          <div className="flex flex-wrap items-center">
            <span
              className="font-bold text-[#23657B]"
            >
              <span itemProp="name">
                {comment.comment_first_name} {comment.comment_last_name}
              </span>
            </span>
            {comment.comment_status === 2 && (
              <span className="mr-2 text-xs text-[#bb1414]">
                سپاس از ارسال دیدگاه‌تون! نظر شما در صف بررسی قرار گرفته و پس از تأیید نمایش داده می‌شود.
              </span>
            )}
          </div>
          <div className="flex items-center">
            <time
              className="text-xs text-[#606060] ml-3"
              dateTime={
                typeof comment.createdAt === "string"
                  ? comment.createdAt
                  : comment.createdAt.toISOString()
              }
            >
              {getTimeAgo(comment.createdAt)}
            </time>
            <span className="sm:block hidden">
              <RatingComment value={comment.comment_rate} onChange={() => { }} disable />
            </span>
          </div>
        </header>
        <hr className="border-[#F5F5F7]" />
        <div className="leading-6 text-[#2E2E2E]">{comment.comment_description}</div>
        <hr className="border-[#F5F5F7]" />
        {comment?.Albums?.length > 0 && (
          <div className="flex items-center">
            {comment.Albums.map((pic) => (
              <Image
                key={pic.id}
                className="ml-1 rounded-md border border-[#23657B]"
                alt={pic.album_image_alt}
                height={40}
                width={40}
                src={process.env.BASE_URL + pic.album_image_src}
              />
            ))}
          </div>
        )}
        {comment.Replies?.length > 0 && (
          <section className="mt-2 space-y-3" aria-label="پاسخ‌ها">
            <h3 className="sr-only">پاسخ‌ها</h3>
            {comment.Replies.map((reply) => {
              let replyBg = "bg-[#F4F4F5] border-[#23657B]";
              let replyText = "text-[#2E2E2E]";
              let roleLabel = null;
              if (reply.comment_role === "support") {
                replyBg = "bg-[#E8F4FF] border-[#23657B]";
                replyText = "text-[#23657B] font-bold";
                roleLabel = (
                  <span className="inline text-xs mx-2 text-[#23657B] font-bold" aria-label="پشتیبانی هیرونیا">
                    (پشتیبانی هیرونیا)
                  </span>
                );
              }
              return (
                <aside
                  key={reply.id}
                  className={`p-2 space-y-2 mx-2 rounded-xl border ${replyBg} transition-all`}
                >
                  <header className="flex flex-wrap justify-between items-center gap-1">
                    <div className="flex flex-wrap items-center">
                      <span className={`font-bold w-max ${replyText}`}

                      >
                        <span>
                          {reply.comment_first_name} {reply.comment_last_name}
                        </span>
                        {roleLabel}
                      </span>
                      {reply.comment_role === "user" && (
                        <>
                          <span className="text-[#606060] mx-1">پاسخ به :</span>
                          <span className="font-bold text-[#23657B]">{comment.comment_first_name} {comment.comment_last_name}</span>
                        </>
                      )}
                    </div>
                    <time
                      className="text-xs text-[#606060]"
                      dateTime={
                        typeof reply.createdAt === "string"
                          ? reply.createdAt
                          : reply.createdAt.toISOString()
                      }
                    >
                      {getTimeAgo(reply.createdAt)}
                    </time>
                  </header>
                  <hr className="border-[#F5F5F7]" />
                  <div className={`leading-6 ${replyText}`}>{reply.comment_description}</div>
                  <hr className="border-[#F5F5F7]" />
                  <footer className="mt-2 flex justify-between items-center">
                    <FavoriteComment
                      id={reply.id}
                      ProductId={productId}
                      ProductIdentityId={productIId}
                      favorites={reply.Favorites}
                      onSuccess={refresh}
                    />
                  </footer>
                </aside>
              );
            })}
          </section>
        )}
        <footer className="flex justify-between items-center">
          <FavoriteComment
            id={comment.id}
            ProductId={productId}
            ProductIdentityId={productIId}
            favorites={comment.Favorites}
            onSuccess={refresh}
          />
          <MyButton
            color="secondary"
            variant="outline"
            className="font-bold"
            onClick={() => openAddComment(comment.id, false)}
          >
            پاسخ
          </MyButton>
        </footer>
      </article>
    ));
  }, [rows, openAddComment, productId, productIId, refresh]);

  return (
    <section className="sm:container mx-auto md:px-1 border border-[#F5F5F7] rounded-xl p-2 my-4 bg-white shadow-sm" aria-label="دیدگاه‌ها">
      {isLoading && renderSkeleton()}

      {!isLoading && error && (
        <p className="text-center text-red-600">خطا در دریافت اطلاعات</p>
      )}

      {!isLoading && !error && rows.length === 0 && (
        <NoData text="تو شروع کن، بقیه همراه میشن! 🚀">
          <MyButton color="primary" onClick={() => openAddComment(null, true)}>
            ارسال دیدگاه
          </MyButton>
        </NoData>
      )}

      {!isLoading && !error && rows.length > 0 && (
        <>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 bg-white p-3 rounded-xl mb-2 shadow">
            <MyButton color="primary" onClick={() => openAddComment(null, true)}>
              ارسال دیدگاه
            </MyButton>
            <div className="flex gap-2">
              <SortButton label="جدیدترین" value="new" />
              <SortButton label="قدیمی‌ترین" value="old" />
            </div>
          </div>
          {commentList}
          {total > 10 && (
            <PaginationScreen
              total={total}
              pages={page}
              onChange={setPage}
              useHref={false}
            />
          )}
        </>
      )}

      <Dialog open={showAddComment} onOpenChange={setShowAddComment}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-center text-[#2E2E2E] font-bold">ارسال دیدگاه</DialogTitle>
          </DialogHeader>
          <AddComment
            productId={productId}
            productIId={productIId}
            commentId={replyTo}
            onSuccess={onSuccess}
            showImages={showImages}
          />
        </DialogContent>
      </Dialog>
    </section>
  );
}
